// components/CommentResponseSettings.tsx
import { useState, useEffect } from 'react';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import {
  updateBio,
  updateSocial,
  createCommonResponse,
  updateCommonResponse,
  deleteCommonResponse,
} from '../../../api/social';
import { useSocial } from '../../../context/socialProvider';
import { Save } from './Save';
import { CommonResponseModal } from './CommonResponseModal';
import { ConfirmationModal } from './ConfirmationModal';

interface CommentResponseSettingsProps {
  selectedSocial: any;
}

const CommentResponseSettings = ({
  selectedSocial,
}: CommentResponseSettingsProps) => {
  const { currentSocial, refreshSocialInfo } = useSocial();

  // Response Instructions State
  const [initialBio, setInitialBio] = useState('');
  const [bio, setBio] = useState('');
  const [isSavingBio, setIsSavingBio] = useState(false);

  // Common Responses State
  const [faqItems, setFaqItems] = useState<any[]>([]);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [editingResponse, setEditingResponse] = useState<any>(null);
  const [faqSaving, setFaqSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(
    null
  );

  // Auto-Generate State
  const [autoGenerateCommentsEnabled, setAutoGenerateCommentsEnabled] =
    useState(false);
  const [
    initialAutoGenerateCommentsEnabled,
    setInitialAutoGenerateCommentsEnabled,
  ] = useState(false);
  const [isSavingAutoGenerate, setIsSavingAutoGenerate] = useState(false);

  // Initialize state from selectedSocial
  useEffect(() => {
    if (selectedSocial) {
      setBio(selectedSocial.bio || '');
      setInitialBio(selectedSocial.bio || '');

      const autoGenerateEnabled =
        selectedSocial?.autoGenerateCommentsEnabled ?? false;
      setAutoGenerateCommentsEnabled(autoGenerateEnabled);
      setInitialAutoGenerateCommentsEnabled(autoGenerateEnabled);

      // Initialize FAQ items
      if (selectedSocial?.commentCommonResponses) {
        const responses = selectedSocial.commentCommonResponses;
        const items = Array.isArray(responses)
          ? responses
          : Object.values(responses).flat();
        setFaqItems(items);
      } else {
        setFaqItems([]);
      }
    }
  }, [selectedSocial]);

  // Response Instructions Functions
  const handleBioSave = async () => {
    if (!selectedSocial) return;
    setIsSavingBio(true);
    try {
      await updateBio(selectedSocial.id, bio);
      if (currentSocial?.id === selectedSocial.id) {
        await refreshSocialInfo();
      }
      setInitialBio(bio);
    } catch (error) {
      console.error('Error saving bio:', error);
    } finally {
      setIsSavingBio(false);
    }
  };

  // Common Responses Functions
  const handleAddResponse = () => {
    setEditingResponse(null);
    setShowResponseModal(true);
  };

  const handleEditResponse = (response: any) => {
    setEditingResponse(response);
    setShowResponseModal(true);
  };

  const handleSaveResponse = async (responseData: {
    question: string;
    answer: string;
  }) => {
    if (!selectedSocial?.id) return;
    setFaqSaving(true);
    try {
      if (editingResponse) {
        const updated = await updateCommonResponse(
          editingResponse.id,
          selectedSocial.id,
          responseData.question,
          responseData.answer
        );
        setFaqItems((prev) =>
          prev.map((faq) => (faq.id === editingResponse.id ? updated : faq))
        );
      } else {
        const newItem = await createCommonResponse(
          selectedSocial.id,
          responseData.question,
          responseData.answer
        );
        setFaqItems((prev) => [...prev, newItem]);
      }
      if (currentSocial?.id === selectedSocial.id) {
        await refreshSocialInfo();
      }
      setShowResponseModal(false);
    } catch (error) {
      console.error('Error saving common response:', error);
    } finally {
      setFaqSaving(false);
    }
  };

  const handleDeleteResponse = async (id: number) => {
    if (!selectedSocial?.id) return;
    setFaqSaving(true);
    try {
      await deleteCommonResponse(id, selectedSocial.id);
      setFaqItems((prev) => prev.filter((faq) => faq.id !== id));
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting common response:', error);
    } finally {
      setFaqSaving(false);
    }
  };

  // Auto-Generate Functions
  const handleToggleAutoGenerateComments = () => {
    setAutoGenerateCommentsEnabled(!autoGenerateCommentsEnabled);
  };

  const handleAutoGenerateSave = async () => {
    if (!selectedSocial) return;
    setIsSavingAutoGenerate(true);
    try {
      await updateSocial(
        selectedSocial.id,
        'autoGenerateCommentsEnabled',
        autoGenerateCommentsEnabled
      );
      setInitialAutoGenerateCommentsEnabled(autoGenerateCommentsEnabled);
      if (currentSocial?.id === selectedSocial.id) {
        await refreshSocialInfo();
      }
    } catch (error) {
      console.error('Failed to save auto-generate comments setting:', error);
      setAutoGenerateCommentsEnabled(initialAutoGenerateCommentsEnabled);
    } finally {
      setIsSavingAutoGenerate(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Common Responses*/}
      <div className="settings-container bio bg-grey rounded-xl p-6 shadow-lg border border-gray-700">
        <div className="mb-4">
          <h3 className="font-semibold text-lg text-white mb-2">
            Common Comment Answers
          </h3>
          <p className="text-sm text-gray-300 mb-4">
            Craft responses to common comments or questions, ensuring your AI
            Agent provides the most relevant and helpful information
          </p>
        </div>

        <div className="flex items-center justify-between mb-4">
          <button
            onClick={handleAddResponse}
            className="flex items-center bg-yellow rounded-xl px-3 py-2 text-black font-semibold hover:brightness-90 active:brightness-75 text-sm"
            disabled={faqSaving}
          >
            <PlusIcon className="w-4 h-4 mr-1" />
            Add Common Response
          </button>
          {faqSaving && <span className="text-gray-400 ml-4">Saving...</span>}
        </div>

        <div className="bg-black border border-gray-600 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-black">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-200">
                  Comment Example
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-200">
                  Suggested Response
                </th>
                <th className="px-4 py-3 text-center text-sm font-medium text-gray-200">
                  {/* Actions */}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {faqItems.map((faq) => (
                <tr key={faq.id} className="hover:bg-gray-800/50">
                  <td className="px-4 py-3 text-sm text-white">
                    {faq.commentExample}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-300">
                    {faq.suggestedOutput}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center space-x-2">
                      <button
                        className="text-gray-400 hover:text-white"
                        onClick={() => handleEditResponse(faq)}
                        disabled={faqSaving}
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(faq.id)}
                        className="text-gray-400 hover:text-red-400"
                        disabled={faqSaving}
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {faqItems.length === 0 && (
                <tr>
                  <td
                    colSpan={3}
                    className="px-4 py-8 text-center text-gray-400"
                  >
                    {/* No common responses added yet. Click "Add Common Response"
                    to get started. */}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Response Instructions */}
      <div className="settings-container bio bg-grey rounded-xl p-6 shadow-lg border border-gray-700">
        <h2 className="font-semibold text-xl text-white mb-4">
          Comment Response Instructions
        </h2>
        <p className="text-sm text-gray-300 mb-4">
          The instructions allow you to customize your agents personality to
          match your brand.
        </p>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Response Instructions
          </label>
          <textarea
            className="bg-black border border-gray-600 rounded p-3 w-full text-sm text-white focus:outline-none focus:ring-2 focus:ring-violet h-80"
            placeholder="Provide specific instructions for how the AI should handle comments..."
            value={bio}
            onChange={(e) => setBio(e.target.value)}
          />
          <Save
            initialValue={initialBio}
            currentValue={bio}
            isLoading={isSavingBio}
            onClick={handleBioSave}
          />
        </div>
      </div>

      {/* Auto-Generate Comment Responses */}
      <div className="settings-container bio bg-grey rounded-xl p-6 shadow-lg border border-gray-700">
        <div className="flex items-start justify-between mb-4">
          <div className="flex flex-col flex-1">
            <div className="flex items-center">
              <h2 className="font-semibold text-xl text-white">
                Auto-Generate Comment Responses
              </h2>
            </div>
            <div className="mt-2 text-sm text-gray-300 space-y-2">
              <div>Generate AI-powered replies to comments on your posts.</div>
              <div className="text-xs text-gray-400">
                Note: Responses are generated only, not auto-posted.
              </div>
              <div className="text-xs text-gray-400">
                Turning this on may slow down comment loading. Turn it off for
                faster loading.
              </div>
            </div>
          </div>
          <div className="flex items-center ml-6">
            <button
              onClick={handleToggleAutoGenerateComments}
              className={`relative inline-flex items-center h-7 w-14 rounded-full transition-colors duration-200 focus:outline-none ${
                autoGenerateCommentsEnabled ? 'bg-violet' : 'bg-gray-400'
              }`}
            >
              <span
                className={`inline-block w-6 h-6 transform bg-white rounded-full shadow transition-transform duration-200 ${
                  autoGenerateCommentsEnabled
                    ? 'translate-x-7'
                    : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
        <div className="flex justify-end">
          <Save
            initialValue={initialAutoGenerateCommentsEnabled}
            currentValue={autoGenerateCommentsEnabled}
            isLoading={isSavingAutoGenerate}
            onClick={handleAutoGenerateSave}
          />
        </div>
      </div>

      {/* Modals */}
      <CommonResponseModal
        isOpen={showResponseModal}
        onClose={() => setShowResponseModal(false)}
        onSave={handleSaveResponse}
        response={editingResponse}
        isLoading={faqSaving}
      />

      <ConfirmationModal
        isOpen={showDeleteConfirm !== null}
        onClose={() => setShowDeleteConfirm(null)}
        onConfirm={() =>
          showDeleteConfirm && handleDeleteResponse(showDeleteConfirm)
        }
        title="Delete Common Response"
        message="Are you sure you want to delete this common response? This action cannot be undone."
      />
    </div>
  );
};

export default CommentResponseSettings;
